
import React, { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { SendHorizontal } from "lucide-react";
import { EmojiPicker } from "./EmojiPicker";
import { AudioRecorder } from "./AudioRecorder";
import { AttachmentUploader, UploadedFile, uploadAttachment } from "./AttachmentUploader";
import { cn } from "@/lib/utils";
import { useToast } from "@/components/ui/use-toast";
import { Attachment } from "./ChatMessages";

interface ChatInputProps {
  value: string;
  onChange: (value: string) => void;
  onSend: (attachments?: Attachment[]) => void;
  disabled?: boolean;
  companyId: string;
}

export const ChatInput: React.FC<ChatInputProps> = ({
  value,
  onChange,
  onSend,
  disabled = false,
  companyId,
}) => {
  const [selectedFiles, setSelectedFiles] = useState<UploadedFile[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { toast } = useToast();

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleEmojiSelect = (emoji: string) => {
    onChange(value + emoji);
    textareaRef.current?.focus();
  };

  const handleSend = async () => {
    if (isUploading || disabled || (!value.trim() && selectedFiles.length === 0)) {
      return;
    }

    if (selectedFiles.length > 0) {
      setIsUploading(true);
      
      try {
        const uploadedAttachments: Attachment[] = [];
        
        // Upload de todos os arquivos selecionados
        for (const file of selectedFiles) {
          const uploadUrl = await uploadAttachment(file.file, companyId);
          
          if (uploadUrl) {
            const attachment: Attachment = {
              id: file.id,
              type: file.type === "image" ? "image" : "document",
              url: uploadUrl,
              name: file.file.name,
              size: file.file.size,
              mimeType: file.file.type
            };
            
            uploadedAttachments.push(attachment);
          }
        }
        
        if (uploadedAttachments.length > 0) {
          onSend(uploadedAttachments);
        } else if (value.trim()) {
          onSend();
        }
        
        // Limpar arquivos e texto após envio
        setSelectedFiles([]);
        onChange("");
      } catch (error) {
        console.error("Erro ao fazer upload dos anexos:", error);
        toast({
          title: "Erro",
          description: "Não foi possível enviar os anexos. Tente novamente.",
          variant: "destructive",
        });
      } finally {
        setIsUploading(false);
      }
    } else if (value.trim()) {
      onSend();
      onChange("");
    }
  };

  const handleAudioRecorded = async (audioAttachment: Attachment) => {
    try {
      onSend([audioAttachment]);
    } catch (error) {
      console.error("Erro ao enviar áudio:", error);
      toast({
        title: "Erro",
        description: "Não foi possível enviar o áudio. Tente novamente.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className={cn(
      "flex flex-col gap-2",
      (isUploading || disabled) && "opacity-70 pointer-events-none"
    )}>
      {selectedFiles.length > 0 && (
        <div className="flex flex-wrap gap-2 p-2 bg-gray-50 rounded-md">
          {selectedFiles.map(file => (
            <div key={file.id} className="relative group">
              {file.type === "image" && file.previewUrl && (
                <img 
                  src={file.previewUrl} 
                  alt={file.file.name}
                  className="h-20 w-20 object-cover rounded-md"
                />
              )}
              {file.type === "document" && (
                <div className="h-20 w-20 flex items-center justify-center bg-gray-200 rounded-md">
                  <span className="text-xs text-center p-1 truncate max-w-full">
                    {file.file.name}
                  </span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      
      <div className="flex items-end gap-2">
        <div className="flex items-center gap-1">
          <AttachmentUploader 
            onAttachmentsSelected={setSelectedFiles}
            selectedFiles={selectedFiles}
          />
          <EmojiPicker onEmojiSelect={handleEmojiSelect} />
        </div>
        
        <div className="flex-1 relative">
          <Textarea
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Digite uma mensagem..."
            className="min-h-[50px] max-h-[150px] pr-12"
            disabled={disabled || isUploading}
            ref={textareaRef}
          />
          
          <div className="absolute right-2 bottom-2">
            <AudioRecorder onAudioRecorded={handleAudioRecorded} />
          </div>
        </div>
        
        <Button
          onClick={handleSend}
          disabled={disabled || isUploading || (!value.trim() && selectedFiles.length === 0)}
          className="h-[50px]"
        >
          <SendHorizontal className="h-5 w-5" />
        </Button>
      </div>
    </div>
  );
};
